import { ErrorBoundary } from '../../utilities/global-error-boundary';
/* eslint-disable no-duplicate-imports */
import { IModuleProps, INodeProps } from '@msdyn365-commerce-modules/utilities';
import Msdyn365, { isUserTypeB2B } from '@msdyn365-commerce/core';
import { SalesOrder } from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';
import classname from 'classnames';
import get from 'lodash/get';
import { action, computed, toJS } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
import { Toaster } from 'sonner';
import { IOrderDetailResponse } from '../../actions/order-details/get-order-details.action';
import { Label } from '../../components/label';
import { ModalProvider } from '../../components/modal';
import { ERROR_CODE_SERVER_ERROR_INDEX } from '../../configuration';
import { ILocalizationState } from '../../states/localization-state';
import { redirectToSignIn } from '../../utilities/is-authenticated';
import { _showToast } from '../../utilities/show-toast';
import { IOrderDetailsData } from './order-details.data';
import { IOrderDetailsProps } from './order-details.props.autogenerated';

export interface IOrderDetailsViewProps extends IOrderDetailsProps<IOrderDetailsData> {
    moduleProps: IModuleProps;
    localizationState: ILocalizationState;
    footerProps?: INodeProps;
    loading?: React.ReactNode;
    heading?: React.ReactNode;
    placedBy?: React.ReactNode;
    orderDetails?: IOrderDetailResponse;
    props?: any;
}

export interface IOrderDetailsState {
    isModalOpen?: boolean;
    isDataReady: boolean;
}

/**
 *
 * OrderDetails component.
 * @extends {React.PureComponent<IOrderDetailsProps<IOrderDetailsData>>}
 */
@observer
class OrderDetails extends React.PureComponent<IOrderDetailsProps<IOrderDetailsData>, IOrderDetailsState> {
    @computed
    get localizationState(): ILocalizationState {
        return this.props.data.localizationState.result as ILocalizationState;
    }

    public async componentDidMount() {
        try {
            await Promise.all([this.props.data.localizationState, this.props.data.getOrderDetails]);
            this.setDataReady();
        } catch (error) {
            if (error instanceof Error && error.message.includes('User is not authenticated')) {
                redirectToSignIn(this.props.context.request.user);
            }
            console.error('Error loading data:', error);
            _showToast(this.props.resources.globalErrorHeading, this.props.resources.globalErrorServiceNotAvailable, 'error');
        }
    }

    @computed
    get orderDetails(): IOrderDetailResponse {
        const rawData = this.props.data.getOrderDetails.result;
        if (rawData?.error) {
            if (rawData.error.errorCode && Number(String(rawData.error.errorCode).charAt(0)) === ERROR_CODE_SERVER_ERROR_INDEX) {
                _showToast(this.props.resources.globalErrorHeading, this.props.resources.globalErrorServiceNotAvailable, 'error');
            }
            return {} as IOrderDetailResponse;
        }
        return toJS(rawData) as IOrderDetailResponse;
    }

    @action
    public setDataReady = () => {
        this.setState({ isDataReady: true });
    };

    @computed
    get order(): SalesOrder | undefined {
        return get(this.props, 'data.orderHydration.result.salesOrder');
    }

    public constructor(props: IOrderDetailsProps<IOrderDetailsData>) {
        super(props);
        this.state = {
            isDataReady: false
        };
    }

    public render(): JSX.Element {
        const {
            resources: {}
        } = this.props;

        const { isDataReady } = this.state;

        if (!isDataReady) {
            return <Label className='ms-order-history__loading' label={this.props.resources.orderDetailsLoadingLabel} />;
        }

        const { salesId = '', transactionId = '' } = this.props.context.actionContext.requestContext.query ?? {};

        let viewProps: IOrderDetailsViewProps = this.getOrderDetailsProps(this.localizationState);
        if (isUserTypeB2B(this.props.context.request)) {
            viewProps = {
                ...viewProps
            };
        }

        if ((salesId || transactionId) && !this.props.context.actionContext.requestContext.user.isAuthenticated) {
            const signInUrl: string = this.props.context.actionContext.requestContext.user.signInUrl ?? '';
            if (Msdyn365.isBrowser) {
                window.location.assign(signInUrl);
            }
        }

        viewProps = {
            ...viewProps
        };

        return (
            <>
                <Toaster
                    richColors
                    position='top-right'
                    toastOptions={{
                        classNames: {
                            default: 'toast-light',
                            error: 'toast-danger',
                            success: 'toast-success',
                            warning: 'toast-warning',
                            info: 'toast-info'
                        }
                    }}
                />
                <ModalProvider props={this.props}>{this.props.renderView(viewProps) as React.ReactElement}</ModalProvider>
            </>
        );
    }

    /**
     * Get the order details view properties.
     * @returns The initial values of order details view properties.
     */
    private readonly getOrderDetailsProps = (localizationState: ILocalizationState): IOrderDetailsViewProps => {
        const moduleProps = { moduleProps: this.props, className: classname('ms-order-details', this.props.config.className) };

        return {
            ...this.props,
            moduleProps,
            footerProps: { className: 'ms-order-details__footer' },
            orderDetails: this.orderDetails,
            props: this.props,
            localizationState: localizationState
        };
    };
}

export default ErrorBoundary(OrderDetails, 'order-details');
